import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import NewsletterForm from "../components/NewsletterForm";
import api from "../lib/api";
import useSeo from "../hooks/useSeo";

const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString("el-GR", { day: "2-digit", month: "long", year: "numeric" }) : "";

export default function Articles() {
  const { t, lang } = useLang();
  const [articles, setArticles] = useState(null);

  useSeo({
    lang,
    title: lang === "en" ? "Sports Science Articles & Guides" : "Άρθρα & Οδηγοί Αθλητικής Επιστήμης",
    description:
      lang === "en"
        ? "Practical sports science articles for coaches and trainers: training methodology, physiology, nutrition, recovery and rehab."
        : "Πρακτικά άρθρα αθλητικής επιστήμης για προπονητές και γυμναστές: μεθοδολογία προπόνησης, φυσιολογία, διατροφή, αποκατάσταση.",
  });

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  return (
    <>
      <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-14 pb-20" data-testid="articles-page">
        <span className="badge-lime">{t("articles.badge")}</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#0B1B3A]" data-testid="articles-title">
          {t("articles.title")}
        </h1>
        <p className="text-base text-[#475569] mt-4 max-w-2xl">{t("articles.subtitle")}</p>

        {articles === null ? (
          <p className="text-[#64748B] mt-10">{t("common.loading")}</p>
        ) : articles.length === 0 ? (
          <div className="card-dark p-12 mt-10 text-center text-[#64748B]" data-testid="articles-empty">{t("articles.empty")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10" data-testid="articles-list">
            {articles.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="card-dark overflow-hidden flex flex-col"
                data-testid={`article-card-${a.slug}`}
              >
                {a.cover_image && <img src={a.cover_image} alt={a.title} className="w-full h-44 object-cover" />}
                <div className="p-6 flex-1 flex flex-col">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8] inline-flex items-center gap-2">
                    <CalendarDays className="w-3.5 h-3.5" /> {fmtDate(a.published_at || a.created_at)}
                  </span>
                  <h2 className="font-heading font-black text-lg md:text-lg text-[#0B1B3A] mt-3 leading-snug">{a.title}</h2>
                  <p className="text-sm text-[#475569] mt-3 flex-1">{a.excerpt}</p>
                  <Link
                    data-testid={`article-read-${a.slug}`}
                    to={`/articles/${a.slug}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#1D4ED8] hover:gap-3 transition-all duration-200"
                  >
                    {t("articles.read")} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-16">
          <NewsletterForm />
        </div>
      </div>
      <Footer />
    </>
  );
}
